import { message } from "antd";
import { api } from "../apiCore";
import {
  ApiResponseType,
  setApplicationBankStatusResponseMessage,
  SetApplicationStatusAction,
} from "./config/config";
import { ApplicationBank } from "../../types/applicationBank.type";
import { errorHandler } from "../../../utils/utilities/apiErrorHandling";

type SendRegistryDto = {
  applicationBankId: number;
  body: {
    number_register: string;
    issuance_bg_date: string;
  };
};

type ApplicationHistoryLog = {
  text: string;
  user: string;
  updated_at: string;
  created_at: string;
};

export type ApplicationHistory = ApplicationHistoryLog[];

// запросы для работы с заявкой для банка
export const applicationBankApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getApplicationDataToBank: builder.query<ApplicationBank, number>({
      query: (applicationBankId) => ({
        url: `auth/system-bank/bank/kamkombank/application-bank/${applicationBankId}`,
      }),
      providesTags: (result, error, applicationBankId) => [
        {
          type: "ApplicationBank",
          id: applicationBankId,
        },
      ],
      transformResponse: (response: { data: ApplicationBank }) => response.data,
    }),

    // сменить статус заявки
    setApplicationBankStatus: builder.mutation<
      ApplicationBank,
      {
        applicationBankId: number;
        action: SetApplicationStatusAction;
      }
    >({
      query: ({ applicationBankId, action }) => {
        return {
          url: `/auth/system-bank/bank/kamkombank/application-bank/${applicationBankId}/${action}`,
          method: "POST",
        };
      },
      async onQueryStarted(
        { applicationBankId, action },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data: updatedApplicationBank } = await queryFulfilled;

          dispatch(
            applicationBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              applicationBankId,
              (draft) => {
                if (draft.modified_bank_status) {
                  draft.modified_bank_status =
                    updatedApplicationBank.modified_bank_status;
                }
              }
            )
          );
          message.success(
            setApplicationBankStatusResponseMessage[action].success
          );
        } catch {
          message.error(setApplicationBankStatusResponseMessage[action].error);
        }
      },
    }),

    // отказать в выдаче БГ. Отдельный сервис из-за того, что в body передается информация с причиной отказа
    rejectApplicationBank: builder.mutation<
      ApplicationBank,
      {
        applicationBankId: number;
        rejectData: {
          textType: string /* textOption или choiceOption */;
          text: string;
        };
      }
    >({
      query: ({ applicationBankId, rejectData }) => {
        return {
          url: `/auth/system-bank/bank/kamkombank/application-bank/${applicationBankId}/refusal`,
          method: "POST",
          body: rejectData,
        };
      },
      async onQueryStarted(
        { applicationBankId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data: updatedApplicationBank } = await queryFulfilled;

          dispatch(
            applicationBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              applicationBankId,
              (draft) => {
                Object.assign(draft, updatedApplicationBank);
              }
            )
          );
          message.success("Успешно отказано"); // написать нормальную обработку ошибок
        } catch {
          message.error("Не удалось отказать");
        }
      },
    }),

    // назначить андеррайтера заявки
    setApplicationUnderwriter: builder.mutation<
      ApplicationBank,
      {
        applicationBankId: number;
        underwriterId: number;
      }
    >({
      query: ({ applicationBankId, underwriterId }) => {
        return {
          url: `/auth/system-bank/bank/kamkombank/application-bank/${applicationBankId}/change-underwriter/${underwriterId}`,
          method: "POST",
        };
      },
      async onQueryStarted(
        { applicationBankId },
        { dispatch, queryFulfilled }
      ) {
        const { responsible_user: changedUser } = (await queryFulfilled).data;

        dispatch(
          applicationBankApi.util.updateQueryData(
            "getApplicationDataToBank",
            applicationBankId,
            (draft) => {
              draft.responsible_user = changedUser;
            }
          )
        );
      },
    }),

    // назначить андеррайтера на новой заявке (для руководителя)
    setNewApplicationUnderwriter: builder.mutation<
      ApplicationBank,
      {
        applicationBankId: number;
        underwriterId: number;
      }
    >({
      query: ({ applicationBankId, underwriterId }) => {
        return {
          url: `/auth/system-bank/bank/kamkombank/application-bank/${applicationBankId}/change-underwriter-new-application/${underwriterId}`,
          method: "POST",
        };
      },
      async onQueryStarted(
        { applicationBankId },
        { dispatch, queryFulfilled }
      ) {
        const { responsible_user: changedUser } = (await queryFulfilled).data;

        dispatch(
          applicationBankApi.util.updateQueryData(
            "getApplicationDataToBank",
            applicationBankId,
            (draft) => {
              draft.responsible_user = changedUser;
            }
          )
        );
      },
    }),

    // назначить сотрудника сопровождения заявки
    setApplicationBankEmployee: builder.mutation<
      ApplicationBank,
      {
        applicationBankId: number;
        bankEmployeeId: number;
      }
    >({
      query: ({ applicationBankId, bankEmployeeId }) => {
        return {
          url: `/auth/system-bank/bank/kamkombank/application-bank/${applicationBankId}/change-employee-user/${bankEmployeeId}`,
          method: "POST",
        };
      },
      async onQueryStarted(
        { applicationBankId },
        { queryFulfilled, dispatch }
      ) {
        const { data: updatedApplicationBank } = await queryFulfilled;

        dispatch(
          applicationBankApi.util.updateQueryData(
            "getApplicationDataToBank",
            applicationBankId,
            (draft) => {
              draft.employee_user = updatedApplicationBank.employee_user;
            }
          )
        );
      },
    }),

    // отправить на проверку регистрационного номера
    sendRegistryNumber: builder.mutation<
      { number_register: number },
      SendRegistryDto
    >({
      query: (sendRegistryDto: SendRegistryDto) => ({
        url: `/auth/system-bank/bank/kamkombank/application-bank/${sendRegistryDto.applicationBankId}/issuance-bg`,
        method: "POST",
        body: sendRegistryDto.body,
      }),
      async onQueryStarted(
        { applicationBankId },
        { queryFulfilled, dispatch }
      ) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            applicationBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              applicationBankId,
              (draft) => {
                draft.number_register = responseData.number_register;
              }
            )
          );
          message.success("Номер успешно изменен");
        } catch (response: unknown) {
          const errorMessage = errorHandler(response as ApiResponseType, [400]);
          message.error(errorMessage);
        }
      },
    }),

    // Загрузить историю заявки (историю действий с заявкой)
    getApplicationHistory: builder.query<ApplicationHistory, number>({
      query: (applicationBankId) => ({
        url: `/auth/system-bank/bank/kamkombank/application-bank/${applicationBankId}/history`,
        method: "GET",
      }),
      transformResponse: (response) => response.message,
    }),
  }),
});

export const {
  useGetApplicationDataToBankQuery,
  useSetApplicationBankStatusMutation,
  useSetNewApplicationUnderwriterMutation,
  useSetApplicationUnderwriterMutation,
  useSendRegistryNumberMutation,
  useGetApplicationHistoryQuery,
  useSetApplicationBankEmployeeMutation,
  useRejectApplicationBankMutation,
} = applicationBankApi;
